import { debounce, type DebounceSettings } from "lodash-es";
import { useCallback, useMemo } from "react";

type ValidationFunction = (value?: string) => Promise<string | undefined> | string | undefined;
// Because of the way Formik handles re-renders we can't just use lodash debounce directly to debounce validation
export const useDebouncedValidation = ({
    validationFunction,
    debounceTime,
    debounceConfig = {},
    dependency,
}: {
    validationFunction: ValidationFunction;
    debounceTime: number;
    debounceConfig?: DebounceSettings;
    dependency: unknown;
}) => {
    const debouncedValidator = useMemo(
        () =>
            debounce(
                async (value: string, callback: (error?: string) => void) => {
                    const error = await validationFunction(value);
                    callback(error);
                },
                debounceTime,
                debounceConfig
            ),
        [dependency]
    );

    return useCallback(
        (value: string) =>
            new Promise<string | undefined>((resolve) => {
                debouncedValidator(value, resolve);
            }),
        [debouncedValidator]
    );
};
